if (!customElements.get('product-state-region')) {
  customElements.define('product-state-region', class extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.maximumHeight = 0;
      this.width = 0;
      this.preorders = [];
      this.requested = false;
      this.fontsReady = false;
      this.measure = this.measure.bind(this);
      this.loadPreorders = this.loadPreorders.bind(this);
      this.onChange = () => this.measure();
      this.product = this.closest('.product');
      this.product?.addEventListener('change', this.onChange, true);
      this.resizeObserver = new ResizeObserver(this.measure);
      this.resizeObserver.observe(this.form);
      this.mutationObserver = new MutationObserver(this.measure);
      this.mutationObserver.observe(this.form, {
        subtree: true, childList: true, characterData: true,
        attributes: true, attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
      });
      document.addEventListener('load', this.loadPreorders, true);
      window.addEventListener('load', this.loadPreorders);
      const onFontsReady = () => {
        if (this.isConnected) {
          this.fontsReady = true;
          this.measure();
        }
      };
      Promise.all([document.fonts.ready, document.fonts.load('italic 13px "Masqualero"')])
        .then(onFontsReady, onFontsReady);
      this.measure();
      this.loadPreorders();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.product?.removeEventListener('change', this.onChange, true);
      document.removeEventListener('load', this.loadPreorders, true);
      window.removeEventListener('load', this.loadPreorders);
    }

    async loadPreorders() {
      if (this.requested || !window.PurpleDot?.fetchWaitlists) return;
      this.requested = true;
      try {
        const waitlists = await window.PurpleDot.fetchWaitlists();
        this.preorders = waitlists.filter((waitlist) => String(waitlist.product?.id) === this.dataset.productId);
        if (this.isConnected) this.measure();
      } catch (error) {
        this.requested = false;
      }
    }

    outerHeight(element) {
      if (!element) return 0;
      const style = getComputedStyle(element);
      if (style.display === 'none') return 0;
      return element.getBoundingClientRect().height +
        (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
    }

    measure() {
      if (!this.fontsReady) return;
      const width = this.form.getBoundingClientRect().width;
      if (!width) return;
      if (Math.abs(width - this.width) > 1) {
        this.width = width;
        this.maximumHeight = 0;
      }

      const buttons = this.form.querySelector('.product-form__buttons--main');
      if (!buttons) return;
      const probe = document.createElement('div');
      probe.className = 'product-state-region__measure product-form__buttons product-form__buttons--main';
      probe.setAttribute('aria-hidden', 'true');
      probe.inert = true;
      probe.style.width = `${buttons.getBoundingClientRect().width}px`;
      this.append(probe);

      const alternatives = Array.from(buttons.querySelectorAll(':scope > .product__only-a-few, :scope > .expected-ship-date, :scope > .no-variant-selected'));
      const currentNoticeHeight = alternatives.reduce((height, notice) => height + this.outerHeight(notice), 0);
      let noticeHeight = currentNoticeHeight;
      alternatives.forEach((notice) => {
        const copy = notice.cloneNode(true);
        copy.style.setProperty('display', 'block', 'important');
        probe.replaceChildren(copy);
        noticeHeight = Math.max(noticeHeight, this.outerHeight(copy));
      });

      const currentPreorderHeight = this.outerHeight(buttons.querySelector('#pd-waitlist-info'));
      this.classList.toggle('product-state-region--preorder', currentPreorderHeight > 0);
      let preorderHeight = currentPreorderHeight;
      const dateFormat = new Intl.DateTimeFormat('en-US', {
        month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Los_Angeles'
      });
      this.preorders.forEach((waitlist) => {
        const info = document.createElement('div');
        info.className = 'product-state-region__preorder';
        const date = new Date(waitlist.shipping_dates?.latest);
        const shipDate = Number.isNaN(date.getTime()) ? waitlist.display_dispatch_date :
          this.dataset.shipDateMessage.replace('[date]', dateFormat.format(date));
        [shipDate, waitlist.payment_plan_descriptions?.long].filter(Boolean).forEach((text) => {
          const paragraph = document.createElement('p');
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          paragraph.textContent = parsed.body.textContent;
          info.append(paragraph);
        });
        probe.replaceChildren(info);
        preorderHeight = Math.max(preorderHeight, this.outerHeight(info));
      });

      // From another state, the preorder layout adds the ship date and the JOIN WAITLIST row, and drops
      // the installments row. Rows measured in the probe already include their 20px bottom margin.
      let preorderExtra = Math.max(0, preorderHeight - currentPreorderHeight);
      if (!currentPreorderHeight && this.preorders.length) {
        const link = buttons.querySelector(':scope > preorder-waitlist button');
        let linkHeight = 0;
        if (link) {
          // Same classes and label, without the product JSON the real button carries.
          const row = document.createElement('div');
          const copy = document.createElement('button');
          copy.className = link.className;
          copy.textContent = link.textContent;
          row.append(copy);
          probe.replaceChildren(row);
          linkHeight = this.outerHeight(row);
        }
        const installments = buttons.querySelector(':scope > .installments-container');
        preorderExtra = preorderHeight + linkHeight - this.outerHeight(installments);
      }
      probe.remove();

      const height = this.form.getBoundingClientRect().height +
        Math.max(0, noticeHeight - currentNoticeHeight) +
        Math.max(0, preorderExtra);
      this.maximumHeight = Math.max(this.maximumHeight, Math.ceil(height));
      const minimum = `${this.maximumHeight}px`;
      if (this.style.getPropertyValue('--product-state-measured-height') !== minimum) {
        this.style.setProperty('--product-state-measured-height', minimum);
      }
    }
  });
}
